
import { CssBaseline } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import Drawer from '@material-ui/core/Drawer';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import { createTheme, MuiThemeProvider, withStyles } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import { WarningOutlined } from '@material-ui/icons';
import LocationIcon from '@material-ui/icons/Business';
import MenuIcon from '@material-ui/icons/Menu';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import LogoutIcon from '@material-ui/icons/PowerSettingsNew';
import { NavItem } from '@sketchpixy/rubix';
import { Entity } from '@sketchpixy/rubix/lib/L20n';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import { forbiddenManagerEmailAddresses, LICENSE_TYPES, RED } from '../_config/consts';
import { getHeaderColorFromThemeName, getLuckeyLogoSource, getPlantName, isProductionDemoSystem, localizeHelpCenterLink } from '../_config/utils';
import { updateLayoutOffsets } from '../_config/layoutMetrics';
import NewspaperIconCustom from '../components/CustomIcons/NewspaperIconCustom.jsx';
import StoreOutlinedIconCustom from '../components/CustomIcons/StoreOutlinedIconCustom.jsx';
import HelpMenuDropdown from '../components/HelpMenuDropdown.jsx';
import LanguageSelector from '../components/LanguageSelector.jsx';
import LocationsMenu from '../components/LocationsMenu.jsx';
import AbilityProvider from '../permissionsUtils/AbilityProvider';
import * as ModalActions from '../redux/actions/modal.actions';
import * as UserActions from '../redux/actions/user.actions';
import * as UtilsActions from '../redux/actions/utils.actions';
import * as WearableActions from '../redux/actions/wearable.actions';
import AppSidebar from './AppSidebar.jsx';
import AppSidebarLimited from './AppSidebarLimited.jsx';
import CreditsIconCustom from '../components/CustomIcons/CreditsIconCustom.jsx';

const drawerWidth = 250;

const styles = theme => ({
  root: {
    // minHeight invece di height fissa: con banner/nome impianto lunghi la
    // toolbar va a capo e l'header deve crescere, altrimenti la seconda riga
    // (azioni) resta nascosta.
    minHeight: 70,
  },
  drawer: {
    [theme.breakpoints.up('sm')]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  appBar: {
    marginLeft: drawerWidth,
    [theme.breakpoints.up('sm')]: {
      width: `calc(100% - ${drawerWidth}px)`,
    },
  },
  menuButton: {
    marginRight: 20,
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  toolbar: theme.mixins.toolbar,
  drawerPaper: {
    width: drawerWidth,
    // top segue l'header reale; bottom:0 + height:auto evita che il drawer
    // sfori sotto il viewport quando l'header va su due righe (altrimenti
    // height:100% di default MUI taglia gli ultimi elementi della sidebar).
    top: 'var(--app-header-height, 65px)',
    bottom: 0,
    height: 'auto',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    zIndex: 900,
    boxShadow: '0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)'
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing.unit * 3,
  },
});

const theme = createTheme({
  palette: {
    primary: { 500: '#f5f6f7' },
  },
  typography: {
    useNextVariants: true,
  },
});

@connect(state => ({ themeName: state.settings.items.theme.data.themeName, userData: state.user.data, domains: state.user.domains, userLicense: state.user.license, whatsNew: state.settings.whatsNew, wallet: state.cloudCredits.wallet, wearables: state.wearables }))
class AppHeader extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showHeader: false,
      showMobileActions: false,
    };
    this.headerRef = React.createRef();
    this.updateHeaderHeight = this.updateHeaderHeight.bind(this);
  }

  toggleMobileActions() {
    const { showMobileActions } = this.state;
    this.setState({ showMobileActions: !showMobileActions });
  }

  // Espone l'altezza REALE dell'header (e della barra lista, se presente) come
  // CSS var, cosi' sidebar e contenuti fixed restano sotto l'header a 1 o 2 righe.
  updateHeaderHeight() {
    updateLayoutOffsets();
  }

  async componentDidMount() {
    const { dispatch } = this.props;
    this.updateHeaderHeight();
    window.addEventListener('resize', this.updateHeaderHeight);
  }

  componentDidUpdate() {
    this.updateHeaderHeight();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateHeaderHeight);
  }

  toggleHeaderMenu() {
    const { showHeader } = this.state;
    this.setState({ showHeader: !showHeader });
  }

  onLogout() {
    const { dispatch } = this.props;
    const params = {
      modalType: 'CONFIRM_TO_CONTINUE_MODAL',
      modalProps: {
        title: 'confirmBeforeContinue',
        message: 'confirmToLogout',
        onConfirmText: <Entity entity="yes" />,
        onConfirm: () => {
          dispatch(UserActions.logout());
          dispatch(ModalActions.hideModal());
        },
        onCancelText: <Entity entity="no" />,
        onCancel: () => dispatch(ModalActions.hideModal()),
      },
    };
    dispatch(ModalActions.showModal(params));
  }

  onOpenOnlineGuide() {
    const formattedURL = localizeHelpCenterLink('');
    window.open(formattedURL);
  }

  onOpenStore() {
    const { dispatch } = this.props;
    dispatch(push('/luckeyStore'));
  }

  async onShowWhatsNew() {
    const { dispatch } = this.props;
    dispatch(UtilsActions.setIsWhatsNewVisible(true));
  }
  
  onOpenSupportTicket() {
    const { dispatch } = this.props;
    dispatch(UtilsActions.setSupportTicketFormVisible(true));
  }

  render() {
    const { classes, themeName, domains, userData, dispatch, whatsNew: { hasUpdate }, wallet, wearables: { activeAlarms } } = this.props;
    const { showHeader, showMobileActions } = this.state;
    const color = getHeaderColorFromThemeName(themeName);
    const plantName = getPlantName();
    const isSupportUser = dispatch(UtilsActions.isSupportUser());
    const isLuckeyLite = AbilityProvider.getAbilityHelper().hasLicenseType([LICENSE_TYPES.LITE]);
    const isLuckeyRFID = AbilityProvider.getAbilityHelper().hasLicenseType([LICENSE_TYPES.RFID]);
    const isLuckeyEngine = AbilityProvider.getAbilityHelper().hasLicenseType([LICENSE_TYPES.ENGINE]);
    const isLuckeyOnPrem = AbilityProvider.getAbilityHelper().hasLicenseType([LICENSE_TYPES.ON_PREMISE]);
    const isMarketPlaceEnabled = dispatch(UserActions.isMarketPlaceEnabled());
    const isFakeCloudCredits = dispatch(UserActions.isFakeCloudCredits());
    const isLuckeyDemoSystem = isProductionDemoSystem();
    const isWalletPendingActivationOrReadyToActivate = dispatch(UserActions.isWalletPendingActivationOrReadyToActivate());
    const luckeyLogoSource = isWalletPendingActivationOrReadyToActivate ? '/imgs/common/logoJagoWhite.png' : getLuckeyLogoSource('white');
    const showLocationMenu = _.indexOf(forbiddenManagerEmailAddresses, _.trim(userData.email)) === -1 && !isLuckeyLite && !isLuckeyOnPrem && !isLuckeyRFID && domains && !_.isEmpty(domains) && !isSupportUser;
    const availableCredits = wallet.availableCredits;
    // Azioni secondarie dell'header, riusate sia dalla toolbar desktop sia dal
    // menu compatto mobile (handler invariati).
    const secondaryActions = (
      <React.Fragment>
        {isMarketPlaceEnabled && !isFakeCloudCredits ? (
          <NavItem onClick={() => this.onOpenStore()} style={{ marginRight: 20, marginTop: 5}}>
          <div style={{ display: 'flex', flexDirection: 'row', marginTop: 0, alignItems: 'center' }}>
            <StoreOutlinedIconCustom style={{ color: 'white', width: 27, marginRight: 10 }} />
            <h4 style={{ color: 'white', margin: 0, marginTop: 0, fontWeight: 'bold' }}><Entity entity="luckeyStore" /></h4>
          </div>
        </NavItem>): null}
        <NavItem onClick={() => this.onShowWhatsNew()} style={{ marginRight: 20, marginTop: 5, position: 'relative' }}>
          <div style={{ display: 'flex', flexDirection: 'row', marginTop: 0, alignItems: 'center' }}>
            <NewspaperIconCustom style={{ color: 'white', width: 27, marginRight: 10 }} />
            <h4 style={{ color: 'white', margin: 0, marginTop: 0, fontWeight: 'bold' }}>{'What\'s new!'}</h4>
            {hasUpdate ? <div style={{ width: 15, height: 15, right: -9, top: 15, borderRadius: 20, backgroundColor: 'gold', position: 'absolute' }} /> : null}
          </div>
        </NavItem>
        {isMarketPlaceEnabled && !isFakeCloudCredits ? (
          <NavItem onClick={() => dispatch(push('/settings?showWallet=true'))} style={{ marginRight: 5, marginTop: 5 }}>
          <div style={{ display: 'flex', flexDirection: 'row', marginTop: 0, alignItems: 'center' }}>
            <CreditsIconCustom style={{ color: 'white', width: 27, marginRight: 10 }} />
            <h4 style={{ color: 'white', margin: 0, marginTop: 0, fontWeight: 'bold' }}>{availableCredits}</h4>
          </div>
        </NavItem>): null}
        {showLocationMenu ? (
          <LocationsMenu domains={domains} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', marginTop: 4, marginRight: 20, marginLeft: 20 }}>
              <LocationIcon style={{ fontSize: 23, marginRight: 12, color: 'white',  }} />
              <h4 style={{ color: 'white', fontWeight: 'bold', marginTop: 12 }}>{plantName}</h4>
            </div>
        )}
        <HelpMenuDropdown
          onOpenOnlineGuide={() => this.onOpenOnlineGuide()}
          onOpenSupportTicket={() => this.onOpenSupportTicket()}
        />
        <div style={{ marginLeft: 25, marginRight: 30, marginBottom: 5 }}>
          <LanguageSelector />
        </div>
      </React.Fragment>
    );
    const logoutButton = (
      <IconButton onClick={() => this.onLogout()}>
        <LogoutIcon style={{ color: 'white', fontSize: 30 }} />
      </IconButton>
    );
    return (
      <MuiThemeProvider theme={theme}>
        <div className={`${classes.root} r-app-header-root`}>
          {/* wrapper misurato: contiene solo le barre header (non la sidebar) */}
          <div ref={this.headerRef}>
          {/* Toolbar desktop completa: nascosta su smartphone (< 600px) */}
          <Hidden xsDown implementation="css">
          <AppBar position="static" style={{ backgroundColor: color, position: 'relative', zIndex: 1000 }}>
          <Toolbar>
            <div style={{ display: 'flex', flex: 1, alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 16, paddingTop: 6, paddingBottom: 6 }}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap', minWidth: 0, flexShrink: 1 }}>
                <img alt="logo" style={{ marginTop: (isLuckeyLite || isLuckeyRFID || isLuckeyEngine || isLuckeyOnPrem || isWalletPendingActivationOrReadyToActivate) ? 0 : -10, height: (isLuckeyLite || isLuckeyRFID || isLuckeyEngine || isLuckeyOnPrem || isWalletPendingActivationOrReadyToActivate) ? 45 : 55, flexShrink: 0 }} src={luckeyLogoSource} />
                {isWalletPendingActivationOrReadyToActivate ? (
                  <div onClick={() => dispatch(push('/settings?showWallet=true'))} style={{ cursor: 'pointer', display: 'flex', flexDirection: 'row', marginLeft: 16, alignItems: 'center', padding: '6px 10px', borderRadius: 10, backgroundColor: 'chocolate', maxWidth: 280, flexShrink: 1, minWidth: 0 }}>
                    <WarningOutlined style={{ color: 'white', fontSize: 22, flexShrink: 0 }} />
                    <div style={{ marginLeft: 8, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <h4 style={{ color: 'white', fontWeight: 'bold', margin: 0, fontSize: 13, lineHeight: 1.2 }}><Entity entity="systemPendingToActivate" /></h4>
                      <h5 style={{ color: 'white', fontWeight: 'bold', margin: 0, fontSize: 11, lineHeight: 1.25, whiteSpace: 'normal' }}><Entity entity="systemPendingToActivateDescription" /></h5>
                    </div>
                  </div>
                ): null}
                {isLuckeyDemoSystem ? (
                  <div style={{ display: 'flex', flexDirection: 'row', marginLeft: 16, alignItems: 'center', padding: '6px 10px', borderRadius: 10, backgroundColor: 'chocolate', maxWidth: 280, flexShrink: 1, minWidth: 0 }}>
                    <WarningOutlined style={{ color: 'white', fontSize: 22, flexShrink: 0 }} />
                    <div style={{ marginLeft: 8, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <h4 style={{ color: 'white', fontWeight: 'bold', margin: 0, fontSize: 13, lineHeight: 1.2 }}><Entity entity="systemIsDemo" /></h4>
                      <h5 style={{ color: 'white', fontWeight: 'bold', margin: 0, fontSize: 11, lineHeight: 1.25, whiteSpace: 'normal' }}><Entity entity="systemIsDemoDescription" /></h5>
                    </div>
                  </div>
                ) : null}
                {activeAlarms && activeAlarms.length > 0 ? (
                  <div onClick={() => dispatch(push('/wearableAlarmsManagement'))} style={{ cursor: 'pointer', display: 'flex', flexDirection: 'row', marginLeft: 16, alignItems: 'center', padding: '6px 10px', borderRadius: 10, backgroundColor: RED, maxWidth: 280, flexShrink: 1, minWidth: 0 }}>
                    <WarningOutlined style={{ color: 'white', fontSize: 22, flexShrink: 0 }} />
                    <div style={{ marginLeft: 8, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <h4 style={{ color: 'white', fontWeight: 'bold', margin: 0, fontSize: 13, lineHeight: 1.2 }}><Entity entity="activeWearableAlarms" /></h4>
                      <h5 style={{ color: 'white', fontWeight: 'bold', margin: 0, fontSize: 11, lineHeight: 1.25, whiteSpace: 'normal' }}><Entity entity="activeAlarmsCount" data={{ count: activeAlarms.length }} /></h5>
                    </div>
                  </div>
                ) : null}
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', rowGap: 16, columnGap: 4 }}>
                {secondaryActions}
                {logoutButton}
              </div>
            </div>
          </Toolbar>
          </AppBar>
          </Hidden>
          <CssBaseline />
          {/* Barra compatta mobile (< 600px): hamburger + logo + logout + menu azioni */}
          <Hidden smUp implementation="css">
            <AppBar position="static" style={{ backgroundColor: color }}>
              <Toolbar style={{ paddingLeft: 4, paddingRight: 4, display: 'flex', alignItems: 'center' }}>
                <IconButton
                  color="inherit"
                  aria-label="Open drawer"
                  onClick={() => this.toggleHeaderMenu()}
                >
                  <MenuIcon style={{ color: 'white', fontSize: 26 }} />
                </IconButton>
                <img alt="luckey" src="/imgs/common/logoJagoWhite.png" style={{ width: 95, marginLeft: 4 }} />
                <div style={{ flex: 1 }} />
                {logoutButton}
                <IconButton color="inherit" aria-label="More actions" onClick={() => this.toggleMobileActions()}>
                  <MoreVertIcon style={{ color: 'white', fontSize: 26 }} />
                </IconButton>
              </Toolbar>
              {showMobileActions ? (
                <div style={{ backgroundColor: color, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', padding: 12, paddingTop: 0, columnGap: 18, rowGap: 8 }}>
                  {secondaryActions}
                </div>
              ) : null}
            </AppBar>
          </Hidden>
          </div>
          <div className={classes.drawer}>
            {/* SHOW ON MOBILE */}
            <Hidden smUp implementation="css">
              <Drawer
                variant="temporary"
                anchor={theme.direction === 'rtl' ? 'right' : 'left'}
                open={showHeader}
                onClose={() => this.toggleHeaderMenu()}
                classes={{
                  paper: classes.drawerPaper,
                }}
                ModalProps={{
                  keepMounted: true, // Better open performance on mobile.
                }}
              >
              {isWalletPendingActivationOrReadyToActivate ? <AppSidebarLimited /> : <AppSidebar />}
              </Drawer>
            </Hidden>
            {/* HIDE ON MOBILE */}
            <Hidden xsDown implementation="css">
              <Drawer
                classes={{
                  paper: classes.drawerPaper,
                }}
                variant="permanent"
                open
              >
                {isWalletPendingActivationOrReadyToActivate ? <AppSidebarLimited /> : <AppSidebar />}
              </Drawer>
            </Hidden>
          </div>
        </div>
      </MuiThemeProvider>
    );
  }
}

const mapStateToProps = state => ({
  utils: state.utils,
  user: state.user,
  wearables: state.wearables,
});

export default connect(mapStateToProps)(withRouter(withStyles(styles)(AppHeader)));
